import * as React from "react";
import { useNavigate } from "react-router-dom";
import Toast from "@ingka/toast";
import styled from "styled-components";
import { useTable } from "react-table";
import TableCell from "../FeaturesOverviewTable/TableCell";
import { TableWrapper } from "../styles";
import Button from "@ingka/button";
import InlineMessage from "@ingka/inline-message";
import Loading, { LoadingBall } from "@ingka/loading";
import { LoadingWrapper, FullHeightContainer } from "../styles";
import AddApplicationModal from "../Applications/AddApplicationModal/AddApplicationModal";
import { useIsFlaggaAdmin } from "../../hooks/useIsFlaggaAdmin";
import { Value } from "../Applications/AddApplicationModal/types";
import DeclineApplicationModal from "./DeclineApplicationModal/DeclineApplicationModal";
import checkmark from "@ingka/ssr-icon/paths/checkmark";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import LoadingLayout from "../LoadingLayout";
import { headerAndFooterHeight } from "../../utils/variables";

interface ManageApplicationRequestsLayoutProps {
	loading: boolean;
	data: any[]; //TODO type
	toastMessage: string;
	setToastMessage: (value: string) => void;
	setShowAddModal: (value: boolean) => void;
	showAddModal: boolean;
	setShowDeclineModal: (value: boolean) => void;
	showDeclineModal: boolean;
	handleCreate: (data: any) => void;
	handlDecline: (data: any) => void;
	value: Value;
	declineAppId: string;
}

// FullHeightContainer pins a fixed viewport height; the table is taller and would slide under the footer
const PageContainer = styled(FullHeightContainer)`
	height: auto;
	min-height: calc(100vh - ${headerAndFooterHeight});
	padding-bottom: 2rem;
`;

const ActionButtonWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	padding: 0.5rem;
	justify-content: flex-start;
	flex-wrap: wrap;
`;

const CellContentWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	min-height: 1.5rem;
`;

const StyledTableWrapper = styled(TableWrapper)`
	margin-top: 1.25rem;
	border-right: transparent;
	border-left: transparent;
	border-top: transparent;
	overflow-x: auto;
	.table {
		table-layout: auto;
		/* 12 columns need more room than the viewport: scroll instead of squeezing cells */
		min-width: 75rem;
	}
	.table-head,
	.table-cell {
		vertical-align: middle;
		text-align: left;
		white-space: nowrap;
		word-break: normal;
		overflow-wrap: normal;
		border-right: transparent !important;
	}
	.table-head[data-id="id"],
	.table-cell[cellId="id"] {
		width: 4rem;
	}
	.table-cell[cellId="action"] {
		white-space: normal;
		max-width: 16rem;
	}
	.table-cell svg {
		flex: 0 0 auto;
	}
`;

const Layout: React.FC<ManageApplicationRequestsLayoutProps> = ({
	loading,
	data,
	toastMessage,
	setShowAddModal,
	showAddModal,
	setShowDeclineModal,
	showDeclineModal,
	handleCreate,
	value,
	handlDecline,
	setToastMessage,
	declineAppId,
}) => {
	const { isFlaggaAdmin } = useIsFlaggaAdmin();
	const navigate = useNavigate();
	if (!isFlaggaAdmin) {
		navigate("/");
	}

	const columns = React.useMemo(
		() => [
			{ id: "id", Header: "ID", accessor: "appId", filter: "fuzzyText" },
			{ Header: "Name", accessor: "appName", filter: "fuzzyText" },
			{ Header: "Admin Email", accessor: "email", filter: "fuzzyText" },
			{ Header: "Has Read Documentation", accessor: "hasReadDocumentation" },
			{ Header: "Show Dashboard", accessor: "showDashboard" },
			{ Header: "Show Market Dashboard", accessor: "showMarketDashboard" },
			{ Header: "Allow ReadOnly", accessor: "allowReadOnly" },
			{ Header: "Schema M2M Update Enabled", accessor: "schemaM2MUpdateEnabled" },
			{ Header: "ClientId", accessor: "clientId" },
			{ Header: "Plan", accessor: "plan" },
			{ Header: "Status", accessor: "status", filter: "fuzzyText" },
			{
				Header: "Action/Reason",
				accessor: "action",
			},
		],
		[]
	);

	const { headerGroups, rows, prepareRow } = useTable({
		columns,
		data,
	});

	return (
		<PageContainer>
			<h1>Application Requests</h1>
			{loading ? (
				<LoadingLayout text="Loading Requests" />
			) : (
				<>
					{!data.length ? (
						<LoadingWrapper>
							<InlineMessage
								title="No Data"
								body={"Seems like you don't have any data"}
								variant="negative"
								headingLevel="2"
							/>
						</LoadingWrapper>
					) : (
						<>
							<StyledTableWrapper>
								<table className="table">
									<thead>
										{headerGroups.map((headerGroup: any) => (
											<tr {...headerGroup.getHeaderGroupProps()}>
												{headerGroup.headers.map((column: any) => (
													<th className="table-head" data-id={column.id} {...column.getHeaderProps()}>
														<>{column.render("Header")}</>
													</th>
												))}
											</tr>
										))}
									</thead>
									<tbody>
										{rows.map((row: any) => {
											prepareRow(row);
											return (
												<tr className="table-row" {...row.getRowProps()}>
													{row.cells.map((cell: any) => (
														<td cellId={cell.column.id} className="table-cell" {...cell.getCellProps()}>
															{cell.column.id != "action" && (
																<CellContentWrapper>
																	<TableCell value={cell.value != null ? cell.value.toString() : ""}></TableCell>
																</CellContentWrapper>
															)}
															{cell.column.id == "action" && row.original.status == "declined" && (
																<CellContentWrapper>
																	<TableCell
																		value={row.original.reason != null ? row.original.reason.toString() : ""}
																	></TableCell>
																</CellContentWrapper>
															)}
															{cell.column.id === "action" && row.original.status == "pending" && (
																<ActionButtonWrapper>
																	{" "}
																	<Button
																		text="Accept"
																		iconPosition="leading"
																		type="emphasised"
																		size="small"
																		ssrIcon={checkmark}
																		onClick={() => {
																			handleCreate(row.original);
																		}}
																		aria-haspopup="dialog"
																	/>
																	<Button
																		text="Decline"
																		size="small"
																		iconPosition="leading"
																		type="tertiary"
																		ssrIcon={trashCan}
																		onClick={() => {
																			handlDecline(row.original.appId);
																		}}
																	/>
																</ActionButtonWrapper>
															)}
														</td>
													))}
												</tr>
											);
										})}
									</tbody>
								</table>
							</StyledTableWrapper>
							<AddApplicationModal showModal={showAddModal} setShowModal={setShowAddModal} valueFromRequest={value} />
							<DeclineApplicationModal
								showModal={showDeclineModal}
								setShowModal={setShowDeclineModal}
								declineAppId={declineAppId}
							/>

							<Toast
								text={toastMessage}
								isOpen={!!toastMessage}
								onCloseRequest={() => setToastMessage("")}
								ariaLabelCloseBtn="Dismiss notification"
							/>
						</>
					)}
				</>
			)}
		</PageContainer>
	);
};

export default Layout;
